import { useDraggable } from "@dnd-kit/core"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { CloseOutlined, HolderOutlined, LockOutlined } from "@ant-design/icons"
import { Tooltip, Typography } from "antd"
import React from "react"

import { fieldIcon } from "./field-icons.tsx"

const { Text } = Typography

interface CardShellProps {
  label: string
  fieldKey: string
  handleProps?: React.HTMLAttributes<HTMLButtonElement>
  onRemove?: () => void
  locked?: boolean
  style?: React.CSSProperties
  setNodeRef?: (node: HTMLElement | null) => void
  attributes?: React.HTMLAttributes<HTMLDivElement>
  disabled?: boolean
}

function CardShell({
  label,
  fieldKey,
  handleProps,
  onRemove,
  locked,
  style,
  setNodeRef,
  attributes,
  disabled
}: CardShellProps) {
  return (
    <div
      className={`flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 ${
        disabled ? "opacity-40" : ""
      }`}
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      {handleProps ? (
        <button
          aria-label={`Drag ${label}`}
          className="cursor-grab text-gray-400 hover:text-gray-600"
          type="button"
          {...handleProps}
        >
          <HolderOutlined />
        </button>
      ) : (
        <span className="text-gray-300">
          <HolderOutlined />
        </span>
      )}
      <span className="text-gray-500">{fieldIcon(fieldKey)}</span>
      <Text className="flex-1 text-sm">{label}</Text>
      {locked && (
        <Tooltip title="Required by the system">
          <LockOutlined className="text-gray-400" />
        </Tooltip>
      )}
      {onRemove && (
        <button
          aria-label={`Remove ${label}`}
          className="text-gray-400 hover:text-red-500"
          onClick={onRemove}
          type="button"
        >
          <CloseOutlined />
        </button>
      )}
    </div>
  )
}

interface AvailableFieldCardProps {
  fieldKey: string
  label: string
  disabled?: boolean
}

/** A field in the left palette — draggable into either requirement group. */
export function AvailableFieldCard({
  fieldKey,
  label,
  disabled
}: AvailableFieldCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `available:${fieldKey}`,
      data: { type: "available", fieldKey },
      disabled
    })

  return (
    <CardShell
      attributes={attributes}
      disabled={disabled}
      fieldKey={fieldKey}
      handleProps={disabled ? undefined : listeners}
      label={label}
      setNodeRef={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.4 : 1
      }}
    />
  )
}

interface PlacedFieldCardProps {
  fieldKey: string
  label: string
  group: string
  onRemove: () => void
}

/** A field already placed in a requirement group — sortable within/between
 * groups, and removable. */
export function PlacedFieldCard({
  fieldKey,
  label,
  group,
  onRemove
}: PlacedFieldCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: `placed:${group}:${fieldKey}`,
    data: { type: "placed", fieldKey, group }
  })

  return (
    <CardShell
      attributes={attributes}
      fieldKey={fieldKey}
      handleProps={listeners}
      label={label}
      onRemove={onRemove}
      setNodeRef={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1
      }}
    />
  )
}

interface LockedFieldCardProps {
  fieldKey: string
  label: string
}

export function LockedFieldCard({ fieldKey, label }: LockedFieldCardProps) {
  return <CardShell fieldKey={fieldKey} label={label} locked />
}

/** The DragOverlay's floating clone. Deliberately hookless — mounting a
 * second useSortable/useDraggable registration for a synthetic id while a
 * real drag is active confuses dnd-kit's own bookkeeping for that drag. */
export function FieldCardPreview({
  fieldKey,
  label
}: {
  fieldKey: string
  label: string
}) {
  return (
    <CardShell
      fieldKey={fieldKey}
      handleProps={{}}
      label={label}
      style={{ cursor: "grabbing" }}
    />
  )
}
